import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { ClothingItem } from './clothing';

export interface Outfit {
  id: string;
  user_id: string;
  name: string;
  items: ClothingItem[];
  preview_url?: string;
  is_public?: boolean;
  occasion?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateOutfitRequest {
  name: string;
  items: string[];
  is_public?: boolean;
  occasion?: string;
}

export interface OutfitShare {
  id: string;
  outfit_id: string;
  outfit?: Outfit;
  sender_id: string;
  sender?: any;
  recipient_id: string;
  recipient?: any;
  status: string;
  feedback_type?: string;
  feedback_notes?: string;
  responded_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ShareOutfitRequest {
  recipient_ids: string[];
  message?: string;
}

export interface FeedbackRequest {
  feedback_type: 'like' | 'dislike';
  feedback_notes?: string;
}

export interface OutfitRating {
  id: string;
  outfit_id: string;
  user_id: string;
  rating: number; // 1-5
  created_at: string;
  updated_at: string;
}

export interface OutfitComment {
  id: string;
  outfit_id: string;
  user_id: string;
  comment: string;
  created_at: string;
  updated_at: string;
}

export interface CreateRatingRequest {
  rating: number; // 1-5
}

export interface CreateCommentRequest {
  comment: string;
}

// Helper to convert Firestore timestamp to ISO string
const timestampToISO = (timestamp: Timestamp | Date | string | undefined): string => {
  if (!timestamp) return new Date().toISOString();
  if (timestamp instanceof Date) return timestamp.toISOString();
  if (typeof timestamp === 'string') return timestamp;
  if (timestamp.toDate) return timestamp.toDate().toISOString();
  return new Date().toISOString();
};

// Helper to convert Firestore document to Outfit
const docToOutfit = async (docData: any): Promise<Outfit> => {
  // Load items
  const items: ClothingItem[] = [];
  if (docData.items && Array.isArray(docData.items)) {
    for (const itemId of docData.items) {
      try {
        const itemDoc = await getDoc(doc(db, 'clothing', itemId));
        if (itemDoc.exists()) {
          const itemData = itemDoc.data();
          items.push({
            id: itemDoc.id,
            user_id: itemData.user_id,
            image_url: itemData.image_url,
            category: itemData.category,
            subcategory: itemData.subcategory,
            color: itemData.color,
            brand: itemData.brand,
            notes: itemData.notes,
            created_at: timestampToISO(itemData.created_at),
            updated_at: timestampToISO(itemData.updated_at),
          });
        }
      } catch (error) {
        console.error(`Error loading item ${itemId}:`, error);
      }
    }
  }

  return {
    id: docData.id,
    user_id: docData.user_id,
    name: docData.name,
    items,
    preview_url: docData.preview_url,
    is_public: docData.is_public || false,
    occasion: docData.occasion,
    created_at: timestampToISO(docData.created_at),
    updated_at: timestampToISO(docData.updated_at),
  };
};

export const outfitService = {
  getAll: async (): Promise<Outfit[]> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const q = query(
      collection(db, 'outfits'),
      where('user_id', '==', user.uid),
      orderBy('created_at', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const outfits = await Promise.all(
      querySnapshot.docs.map((doc) => docToOutfit({ id: doc.id, ...doc.data() }))
    );
    return outfits;
  },

  getById: async (id: string): Promise<Outfit> => {
    const docRef = doc(db, 'outfits', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Outfit not found');
    return docToOutfit({ id: docSnap.id, ...docSnap.data() });
  },

  create: async (data: CreateOutfitRequest): Promise<Outfit> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const docRef = await addDoc(collection(db, 'outfits'), {
      user_id: user.uid,
      name: data.name,
      items: data.items,
      is_public: data.is_public || false,
      occasion: data.occasion || null,
      preview_url: null,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });

    // Generate preview via Cloud Function
    if (data.items.length > 0) {
      try {
        const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import('firebase/functions');
        const functions = getFunctions();
        
        // Connect to emulator in development
        const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true' || process.env.NODE_ENV === 'development';
        if (useEmulators) {
          try {
            connectFunctionsEmulator(functions, 'localhost', 5001);
          } catch (e) {
            // Already connected
          }
        }
        
        const generateOutfitPreview = httpsCallable(functions, 'generateOutfitPreview');
        const result = await generateOutfitPreview({ outfitId: docRef.id, itemIds: data.items });
        
        if (result.data) {
          const data = result.data as { preview_url?: string };
          if (data.preview_url) {
            await updateDoc(docRef, {
              preview_url: data.preview_url,
              updated_at: serverTimestamp(),
            });
          }
        }
      } catch (error) {
        console.error('Failed to generate preview:', error);
      }
    }

    const docSnap = await getDoc(docRef);
    return docToOutfit({ id: docSnap.id, ...docSnap.data() });
  },

  update: async (id: string, data: Partial<CreateOutfitRequest>): Promise<Outfit> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const docRef = doc(db, 'outfits', id);
    const updateData: any = {
      updated_at: serverTimestamp(),
    };

    if (data.name !== undefined) updateData.name = data.name;
    if (data.items !== undefined) updateData.items = data.items;
    if (data.is_public !== undefined) updateData.is_public = data.is_public;
    if (data.occasion !== undefined) updateData.occasion = data.occasion;

    await updateDoc(docRef, updateData);
    const docSnap = await getDoc(docRef);
    return docToOutfit({ id: docSnap.id, ...docSnap.data() });
  },

  delete: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'outfits', id));
  },

  share: async (id: string, data: ShareOutfitRequest): Promise<OutfitShare[]> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const shares: OutfitShare[] = [];
    for (const recipientId of data.recipient_ids) {
      const shareRef = await addDoc(collection(db, 'outfit_shares'), {
        outfit_id: id,
        sender_id: user.uid,
        recipient_id: recipientId,
        status: 'pending',
        feedback_notes: data.message || null,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      const shareDoc = await getDoc(shareRef);
      shares.push({
        id: shareDoc.id,
        outfit_id: id,
        sender_id: user.uid,
        recipient_id: recipientId,
        status: 'pending',
        feedback_notes: data.message,
        created_at: timestampToISO(shareDoc.data()?.created_at),
        updated_at: timestampToISO(shareDoc.data()?.updated_at),
      });
    }
    return shares;
  },

  getReceivedShares: async (): Promise<OutfitShare[]> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const q = query(
      collection(db, 'outfit_shares'),
      where('recipient_id', '==', user.uid)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      created_at: timestampToISO(doc.data().created_at),
      updated_at: timestampToISO(doc.data().updated_at),
    })) as OutfitShare[];
  },

  getSentShares: async (): Promise<OutfitShare[]> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const q = query(
      collection(db, 'outfit_shares'),
      where('sender_id', '==', user.uid)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      created_at: timestampToISO(doc.data().created_at),
      updated_at: timestampToISO(doc.data().updated_at),
    })) as OutfitShare[];
  },

  getShare: async (id: string): Promise<OutfitShare> => {
    const docRef = doc(db, 'outfit_shares', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Share not found');
    return {
      id: docSnap.id,
      ...docSnap.data(),
      created_at: timestampToISO(docSnap.data().created_at),
      updated_at: timestampToISO(docSnap.data().updated_at),
    } as OutfitShare;
  },

  submitFeedback: async (id: string, data: FeedbackRequest): Promise<OutfitShare> => {
    const docRef = doc(db, 'outfit_shares', id);
    await updateDoc(docRef, {
      status: 'responded',
      feedback_type: data.feedback_type,
      feedback_notes: data.feedback_notes || null,
      responded_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
    const docSnap = await getDoc(docRef);
    return {
      id: docSnap.id,
      ...docSnap.data(),
      created_at: timestampToISO(docSnap.data()?.created_at),
      updated_at: timestampToISO(docSnap.data()?.updated_at),
    } as OutfitShare;
  },

  generatePreview: async (itemIds: string[]): Promise<{ preview_url: string }> => {
    const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import('firebase/functions');
    const functions = getFunctions();
    
    // Connect to emulator in development
    const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true';
    if (useEmulators) {
      try {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      } catch (e) {
        // Already connected
      }
    }
    
    const generateOutfitPreview = httpsCallable(functions, 'generateOutfitPreview');
    const result = await generateOutfitPreview({ itemIds });
    return result.data as { preview_url: string };
  },

  getPublic: async (): Promise<Outfit[]> => {
    const q = query(
      collection(db, 'outfits'),
      where('is_public', '==', true),
      orderBy('created_at', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const outfits = await Promise.all(
      querySnapshot.docs.map((doc) => docToOutfit({ id: doc.id, ...doc.data() }))
    );
    return outfits;
  },

  // Comments
  getComments: async (outfitId: string): Promise<OutfitComment[]> => {
    const q = query(
      collection(db, 'outfit_comments'),
      where('outfit_id', '==', outfitId),
      orderBy('created_at', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      outfit_id: doc.data().outfit_id,
      user_id: doc.data().user_id,
      comment: doc.data().comment,
      created_at: timestampToISO(doc.data().created_at),
      updated_at: timestampToISO(doc.data().updated_at),
    }));
  },

  createComment: async (outfitId: string, data: CreateCommentRequest): Promise<OutfitComment> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const docRef = await addDoc(collection(db, 'outfit_comments'), {
      outfit_id: outfitId,
      user_id: user.uid,
      comment: data.comment,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });

    const docSnap = await getDoc(docRef);
    return {
      id: docSnap.id,
      outfit_id: outfitId,
      user_id: user.uid,
      comment: data.comment,
      created_at: timestampToISO(docSnap.data()?.created_at),
      updated_at: timestampToISO(docSnap.data()?.updated_at),
    };
  },

  // Ratings
  getRatings: async (outfitId: string): Promise<OutfitRating[]> => {
    const q = query(
      collection(db, 'outfit_ratings'),
      where('outfit_id', '==', outfitId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      outfit_id: doc.data().outfit_id,
      user_id: doc.data().user_id,
      rating: doc.data().rating,
      created_at: timestampToISO(doc.data().created_at),
      updated_at: timestampToISO(doc.data().updated_at),
    }));
  },

  createOrUpdateRating: async (outfitId: string, data: CreateRatingRequest): Promise<OutfitRating> => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Check if user already rated this outfit
    const existingQuery = query(
      collection(db, 'outfit_ratings'),
      where('outfit_id', '==', outfitId),
      where('user_id', '==', user.uid)
    );
    const existingSnapshot = await getDocs(existingQuery);

    if (!existingSnapshot.empty) {
      // Update existing rating
      const existingDoc = existingSnapshot.docs[0];
      await updateDoc(doc(db, 'outfit_ratings', existingDoc.id), {
        rating: data.rating,
        updated_at: serverTimestamp(),
      });
      const updatedDoc = await getDoc(doc(db, 'outfit_ratings', existingDoc.id));
      return {
        id: updatedDoc.id,
        outfit_id: outfitId,
        user_id: user.uid,
        rating: data.rating,
        created_at: timestampToISO(updatedDoc.data()?.created_at),
        updated_at: timestampToISO(updatedDoc.data()?.updated_at),
      };
    } else {
      // Create new rating
      const docRef = await addDoc(collection(db, 'outfit_ratings'), {
        outfit_id: outfitId,
        user_id: user.uid,
        rating: data.rating,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      const docSnap = await getDoc(docRef);
      return {
        id: docSnap.id,
        outfit_id: outfitId,
        user_id: user.uid,
        rating: data.rating,
        created_at: timestampToISO(docSnap.data()?.created_at),
        updated_at: timestampToISO(docSnap.data()?.updated_at),
      };
    }
  },

  getAverageRating: async (outfitId: string): Promise<number> => {
    try {
      // Query ratings directly to avoid circular reference
      const q = query(
        collection(db, 'outfit_ratings'),
        where('outfit_id', '==', outfitId)
      );
      const querySnapshot = await getDocs(q);
      const ratings = querySnapshot.docs.map((doc) => doc.data().rating);
      
      if (ratings.length === 0) return 0;
      const sum = ratings.reduce((acc, rating) => acc + rating, 0);
      const average = sum / ratings.length;
      return Math.round(average * 10) / 10; // Round to 1 decimal place
    } catch (err) {
      console.error(`Failed to get average rating for outfit ${outfitId}:`, err);
      return 0;
    }
  },
};
